// Quack City 3D — flagship shopping-street scene. Best-in-class web 3D:
// PBR materials, image-based lighting (RoomEnvironment), soft shadows, ACES tone
// mapping + bloom, third-person follow camera, ambient pedestrians, duck pet.
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { makeHuman, makeDuck, randHumanOpts } from "./character";

const TIERS = {
  high: { dpr: 2, shadow: 2048, bloom: true, peds: 9, lamps: true },
  medium: { dpr: 1.5, shadow: 1024, bloom: true, peds: 6, lamps: true },
  low: { dpr: 1, shadow: 0, bloom: false, peds: 3, lamps: false },
};
export function autoTier() {
  const mem = navigator.deviceMemory || 4; const cores = navigator.hardwareConcurrency || 4;
  const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  if (mem <= 3 || cores <= 3) return "low";
  if (mobile || mem <= 6) return "medium";
  return "high";
}

const std = (color, rough = 0.85, metal = 0) => new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: rough, metalness: metal });

function tileTexture() {
  const c = document.createElement("canvas"); c.width = c.height = 256; const x = c.getContext("2d");
  x.fillStyle = "#e8a679"; x.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) {
    const shade = ["#efb488", "#e6a06f", "#f0bd96", "#e29a6a"][(i + j) % 4];
    x.fillStyle = shade; const p = 4; x.fillRect(i * 64 + p, j * 64 + p, 64 - p * 2, 64 - p * 2);
  }
  x.strokeStyle = "rgba(120,70,40,0.25)"; x.lineWidth = 2;
  for (let i = 0; i <= 4; i++) { x.beginPath(); x.moveTo(i * 64, 0); x.lineTo(i * 64, 256); x.moveTo(0, i * 64); x.lineTo(256, i * 64); x.stroke(); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(14, 14); t.anisotropy = 4; t.colorSpace = THREE.SRGBColorSpace; return t;
}

function skyDome() {
  const geo = new THREE.SphereGeometry(200, 24, 16); const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide, uniforms: { top: { value: new THREE.Color("#8ec5e6") }, bot: { value: new THREE.Color("#ffe6c9") } },
    vertexShader: "varying vec3 p; void main(){ p=position; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);} ",
    fragmentShader: "varying vec3 p; uniform vec3 top; uniform vec3 bot; void main(){ float h=normalize(p).y*0.5+0.5; gl_FragColor=vec4(mix(bot,top,clamp(h,0.0,1.0)),1.0);} ",
  });
  return new THREE.Mesh(geo, mat);
}

export function createWorld3D({ container, quality, onProximity, onInteract }) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.outputColorSpace = THREE.SRGBColorSpace; renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 0.92;
  renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.style.width = "100%"; renderer.domElement.style.height = "100%"; renderer.domElement.style.display = "block";
  renderer.domElement.style.touchAction = "none";
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene(); scene.fog = new THREE.Fog("#dfe9f0", 34, 90);
  const sky = skyDome(); scene.add(sky);
  const pmrem = new THREE.PMREMGenerator(renderer); scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture; scene.environmentIntensity = 0.35;
  const camera = new THREE.PerspectiveCamera(58, 1, 0.1, 260);

  // Lighting — warm daylight
  const hemi = new THREE.HemisphereLight(0xffe8c8, 0x9a7b58, 0.42); scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xfff2d6, 1.15); sun.position.set(14, 22, 10); sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048); sun.shadow.camera.near = 1; sun.shadow.camera.far = 80;
  sun.shadow.camera.left = -24; sun.shadow.camera.right = 24; sun.shadow.camera.top = 24; sun.shadow.camera.bottom = -24; sun.shadow.bias = -0.0004;
  scene.add(sun); scene.add(sun.target);

  // Ground
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(120, 120), new THREE.MeshStandardMaterial({ map: tileTexture(), roughness: 0.95 }));
  ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; scene.add(ground);
  // side grass strips
  for (const sx of [-22, 22]) { const g = new THREE.Mesh(new THREE.BoxGeometry(20, 0.2, 120), std("#5e8d54", 1)); g.position.set(sx, 0.0, 0); g.receiveShadow = true; scene.add(g); }

  const obstacles = []; // {x,z,r}
  const interactables = []; // {id,todo,label,x,z,r}
  const pushBox = (x, z, w, d) => obstacles.push({ x, z, r: Math.max(w, d) * 0.5 + 0.35 });

  // ---- Building shells along the back ----
  function building(x, w, h, color) {
    const b = new THREE.Group(); b.position.set(x, 0, -8);
    const wall = new THREE.Mesh(new THREE.BoxGeometry(w, h, 6), std(color, 0.9)); wall.position.y = h / 2; wall.position.z = -3; wall.castShadow = true; wall.receiveShadow = true; b.add(wall);
    // windows (emissive)
    for (let i = 0; i < Math.max(2, Math.floor(w / 2)); i++) for (let fl = 1; fl < Math.max(2, Math.floor(h / 2.4)); fl++) {
      const win = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.1, 0.1), new THREE.MeshStandardMaterial({ color: "#fff4d6", emissive: "#ffdf9e", emissiveIntensity: 0.18, roughness: 0.4 }));
      win.position.set(-w / 2 + 1 + i * 2, fl * 2.4, 0.05); b.add(win);
    }
    scene.add(b); return b;
  }
  building(-14, 12, 9, "#c9a27a"); building(0, 12, 11, "#b98d63"); building(14, 12, 8, "#caa9c9");

  // ---- Storefront factory ----
  function awning(parent, x, w, color) {
    const a = new THREE.Mesh(new THREE.BoxGeometry(w, 0.16, 1.6), std(color, 0.8)); a.position.set(x, 2.7, -4.1); a.castShadow = true; parent.add(a);
    for (let i = 0; i <= w; i += 0.6) { const st = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.16, 1.6), std(i % 1.2 < 0.6 ? "#ffffff" : color, 0.8)); st.position.set(x - w / 2 + i, 2.72, -4.05); parent.add(st); }
  }
  function signBoard(x, y, text, bg) {
    const c = document.createElement("canvas"); c.width = 512; c.height = 160; const g = c.getContext("2d");
    g.fillStyle = bg; g.fillRect(0, 0, 512, 160); g.fillStyle = "#fff"; g.font = "bold 78px Georgia"; g.textAlign = "center"; g.textBaseline = "middle"; g.fillText(text, 256, 84);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const m = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1, 0.15), new THREE.MeshStandardMaterial({ map: tex, roughness: 0.6 })); m.position.set(x, y, -4.0); scene.add(m); return m;
  }
  function counter(x, color) {
    const grp = new THREE.Group(); grp.position.set(x, 0, -5.2); scene.add(grp);
    const base = new THREE.Mesh(new THREE.BoxGeometry(3.4, 1.05, 1.4), std(color, 0.85)); base.position.y = 0.52; base.castShadow = true; base.receiveShadow = true; grp.add(base);
    const glass = new THREE.Mesh(new THREE.BoxGeometry(3.3, 0.7, 1.2), new THREE.MeshPhysicalMaterial({ color: "#dff", roughness: 0.05, metalness: 0, transmission: 0.9, transparent: true, opacity: 0.35, thickness: 0.5 }));
    glass.position.y = 1.4; grp.add(glass);
    pushBox(x, -5.2, 3.4, 1.4);
    return grp;
  }

  // Ice cream (Grab a bite)
  const ice = counter(-14, "#f6a8c0"); awning(scene, -14, 3.6, "#ec6 f9a".replace(" ", ""));
  signBoard(-14, 4.1, "Ice Cream", "#e26d97");
  for (let i = 0; i < 4; i++) { const flavor = ["#f9c8d4", "#c3e6c9", "#f6d69a", "#b79ae0"][i]; const scoop = new THREE.Mesh(new THREE.SphereGeometry(0.16, 14, 14), std(flavor, 0.7)); scoop.position.set(-15 + i * 0.55, 1.15, -4.9); scene.add(scoop); }
  const bigCone = new THREE.Group(); bigCone.position.set(-11.4, 1.4, -4.6); scene.add(bigCone);
  bigCone.add(new THREE.Mesh(new THREE.ConeGeometry(0.35, 0.8, 16), std("#e0a35b", 0.8)));
  const swirl = new THREE.Mesh(new THREE.SphereGeometry(0.34, 16, 16), new THREE.MeshStandardMaterial({ color: "#fbd0e0", roughness: 0.5, emissive: "#f6a8c0", emissiveIntensity: 0.25 })); swirl.position.y = 0.55; bigCone.add(swirl);
  interactables.push({ id: "icecream", todo: "bite", label: "Ice Cream — Grab a bite", x: -14, z: -3.8, r: 3.2, coins: 12 });

  // Produce market (Buy groceries)
  counter(-4.5, "#9c6b3f"); awning(scene, -4.5, 3.6, "#5aa06a"); signBoard(-4.5, 4.1, "Market", "#3e8e57");
  const fruitColors = ["#e0452f", "#f6a623", "#7bbf3a", "#f2d43a", "#d94f8a"];
  for (let i = 0; i < 10; i++) { const f = new THREE.Mesh(new THREE.SphereGeometry(0.13 + Math.random() * 0.05, 12, 12), std(fruitColors[i % 5], 0.7)); f.position.set(-5.6 + (i % 5) * 0.42, 1.18 + (i > 4 ? 0.24 : 0), -4.9 + (i > 4 ? -0.1 : 0)); f.castShadow = true; scene.add(f); }
  const crate = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.5, 0.9), std("#8a5a34", 0.9)); crate.position.set(-3.2, 0.85, -4.6); crate.castShadow = true; scene.add(crate);
  interactables.push({ id: "market", todo: "groceries", label: "Market — Buy groceries", x: -4.5, z: -3.8, r: 3.2, coins: 14 });

  // Drinks kiosk (Get a drink)
  counter(4.5, "#3b7ea1"); awning(scene, 4.5, 3.6, "#2f9bbf"); signBoard(4.5, 4.1, "Drinks", "#2f8fb0");
  for (let i = 0; i < 6; i++) { const b = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.34, 10), std(["#ea5a5a", "#f6b93b", "#4ad0a0", "#e56ab0", "#6aa0e5", "#f2f2f2"][i], 0.4, 0.1)); b.position.set(3.5 + i * 0.4, 1.32, -4.9); scene.add(b); }
  interactables.push({ id: "drinks", todo: "drink", label: "Drinks — Get a drink", x: 4.5, z: -3.8, r: 3.2, coins: 10 });

  // Boutique (Snag a dress)
  counter(14, "#b06bd0"); awning(scene, 14, 3.6, "#c07bd8"); signBoard(14, 4.1, "Boutique", "#a24fc0");
  const rack = new THREE.Group(); rack.position.set(13.4, 0, -4.7); scene.add(rack);
  const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 2, 8), std("#cfcfcf", 0.4, 0.6)); bar.rotation.z = Math.PI / 2; bar.position.y = 1.7; rack.add(bar);
  for (let i = 0; i < 5; i++) { const dress = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.7, 8), std(["#e05a8a", "#5ab0e0", "#f2c14e", "#8a6bd0", "#4ad0a0"][i], 0.8)); dress.position.set(-0.8 + i * 0.4, 1.25, 0); rack.add(dress); }
  // mannequin
  const mann = makeHuman({ skin: "#d9c3b0", hair: "#3a2a1a", shirt: "#e05a8a", pants: "#e05a8a", shoes: "#7a3a5a", female: true, scale: 1 }); mann.group.position.set(14.9, 0, -4.3); mann.group.rotation.y = Math.PI; scene.add(mann.group);
  interactables.push({ id: "boutique", todo: "dress", label: "Boutique — Snag a dress", x: 14, z: -3.8, r: 3.2, coins: 16 });

  // Pet supplies (Buy cat food)
  const petStand = new THREE.Group(); petStand.position.set(9, 0, -5.2); scene.add(petStand);
  const pbase = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1, 1.2), std("#c98a3a", 0.85)); pbase.position.y = 0.5; pbase.castShadow = true; petStand.add(pbase);
  for (let i = 0; i < 3; i++) { const bag = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.5, 0.3), std(["#e0762f", "#4a90d9", "#6abf4a"][i], 0.9)); bag.position.set(-0.6 + i * 0.6, 1.3, 0); petStand.add(bag); }
  signBoard(9, 3.6, "Pet Shop", "#c9772f"); pushBox(9, -5.2, 2.2, 1.2);
  interactables.push({ id: "pet", todo: "catfood", label: "Pet Shop — Buy cat food", x: 9, z: -3.8, r: 3.2, coins: 10 });

  // ---- Street props ----
  const bulbs = [];
  const tier0 = TIERS[quality] || TIERS.high;
  function lamp(x, z) {
    const g = new THREE.Group(); g.position.set(x, 0, z);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 3.2, 10), std("#3a3f47", 0.5, 0.6)); pole.position.y = 1.6; pole.castShadow = true; g.add(pole);
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 12), new THREE.MeshStandardMaterial({ color: "#fff6d8", emissive: "#ffdf9e", emissiveIntensity: 1.1 })); bulb.position.y = 3.3; g.add(bulb); bulbs.push(bulb);
    if (tier0.lamps) { const pl = new THREE.PointLight(0xffe6b0, 0.5, 8, 2); pl.position.set(x, 3.2, z); scene.add(pl); }
    scene.add(g); pushBox(x, z, 0.4, 0.4);
  }
  function bench(x, z, ry = 0) { const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = ry; const seat = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.12, 0.5), std("#8a5a34", 0.8)); seat.position.y = 0.5; seat.castShadow = true; g.add(seat); const back = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.4, 0.1), std("#8a5a34", 0.8)); back.position.set(0, 0.75, -0.2); g.add(back); scene.add(g); pushBox(x, z, 1.6, 0.5); }
  function planter(x, z) { const g = new THREE.Group(); g.position.set(x, 0, z); const box = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.5, 0.9), std("#7a5a3a", 0.9)); box.position.y = 0.25; box.castShadow = true; g.add(box); for (let i = 0; i < 3; i++) { const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 10), std(["#4a8a3a", "#5aa04a", "#3f7a34"][i], 0.9)); leaf.position.set((i - 1) * 0.22, 0.7, 0); leaf.scale.y = 1.3; g.add(leaf); } scene.add(g); pushBox(x, z, 0.9, 0.9); }
  for (const x of [-18, -9, 9, 18]) lamp(x, 3.5);
  bench(-6, 4.5, 0); bench(6, 4.5, 0); planter(-2, 3); planter(2, 3); planter(-11, 3); planter(11, 3);

  // Central fountain
  const fountain = new THREE.Group(); fountain.position.set(0, 0, 7); scene.add(fountain);
  const basin = new THREE.Mesh(new THREE.CylinderGeometry(2, 2.2, 0.6, 28), std("#b8c2cc", 0.7)); basin.position.y = 0.3; basin.castShadow = true; basin.receiveShadow = true; fountain.add(basin);
  const water = new THREE.Mesh(new THREE.CylinderGeometry(1.8, 1.8, 0.1, 28), new THREE.MeshStandardMaterial({ color: "#3f9ec0", roughness: 0.55, metalness: 0.0, transparent: true, opacity: 0.85 })); water.position.y = 0.56; fountain.add(water);
  const spout = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.2, 1.2, 12), std("#b8c2cc", 0.7)); spout.position.y = 1.1; fountain.add(spout);
  obstacles.push({ x: 0, z: 7, r: 2.6 });

  // ---- Player ----
  const player = makeHuman({ skin: "#e0a877", hair: "#2b1b0e", shirt: "#2f6f5e", pants: "#38414f", shoes: "#1a1a1a", female: false, scale: 1.02 });
  player.group.position.set(0, 0, 2); scene.add(player.group);
  let playerYaw = Math.PI; player.group.rotation.y = playerYaw;

  // Pet duck
  const pet = makeDuck({ color: "#fbbf24", scale: 1 }); pet.group.position.set(1, 0, 3); scene.add(pet.group);

  // ---- Pedestrians ----
  const peds = [];
  const WALK = [[-18, 1.5], [18, 1.5], [-10, 5], [10, 5], [0, 3], [-16, 4], [16, 4]];
  for (let i = 0; i < tier0.peds; i++) {
    const h = makeHuman(randHumanOpts());
    const start = WALK[i % WALK.length]; h.group.position.set(start[0] + (Math.random() - 0.5) * 3, 0, start[1] + (Math.random() - 0.5) * 2);
    scene.add(h.group);
    peds.push({ h, target: pickWalkTarget(), yaw: Math.random() * 6, speed: 1.1 + Math.random() * 0.6 });
  }
  function pickWalkTarget() { return new THREE.Vector3(-19 + Math.random() * 38, 0, 1.2 + Math.random() * 4.5); }

  // ---- Camera state ----
  let camYaw = 0, camPitch = 0.32, camDist = 6.2;
  const camTarget = new THREE.Vector3();
  camera.position.set(0, 4, 8);

  // ---- Input ----
  const keys = {}; const moveAxis = { x: 0, y: 0 }; let joyAxis = { x: 0, y: 0 };
  const onKeyDown = (e) => { const k = e.key.toLowerCase(); keys[k] = true; if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(k)) e.preventDefault(); if (k === "e") tryInteract(); };
  const onKeyUp = (e) => { keys[e.key.toLowerCase()] = false; };
  window.addEventListener("keydown", onKeyDown); window.addEventListener("keyup", onKeyUp);

  let dragging = false, lastX = 0, lastY = 0, dragId = null;
  const el = renderer.domElement;
  const onPointerDown = (e) => { dragging = true; dragId = e.pointerId; lastX = e.clientX; lastY = e.clientY; el.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => { if (!dragging || e.pointerId !== dragId) return; const dx = e.clientX - lastX, dy = e.clientY - lastY; lastX = e.clientX; lastY = e.clientY; camYaw -= dx * 0.006; camPitch = Math.max(0.05, Math.min(0.85, camPitch + dy * 0.004)); };
  const onPointerUp = (e) => { if (e.pointerId === dragId) { dragging = false; dragId = null; } };
  const onWheel = (e) => { camDist = Math.max(3.2, Math.min(11, camDist + e.deltaY * 0.01)); e.preventDefault(); };
  el.addEventListener("pointerdown", onPointerDown); el.addEventListener("pointermove", onPointerMove); window.addEventListener("pointerup", onPointerUp); el.addEventListener("wheel", onWheel, { passive: false });

  let nearest = null;
  const tryInteract = () => { if (nearest) onInteract?.(nearest); };

  // ---- Post ----
  let composer = null, bloomPass = null; let postOn = tier0.bloom;
  function buildComposer() {
    composer = new EffectComposer(renderer); composer.addPass(new RenderPass(scene, camera));
    bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.14, 0.4, 0.9); composer.addPass(bloomPass);
    composer.addPass(new OutputPass());
  }
  if (postOn) buildComposer();

  // ---- Sizing ----
  function resize() {
    const w = container.clientWidth || 1, h = container.clientHeight || 1;
    renderer.setPixelRatio(Math.min((TIERS[quality] || TIERS.high).dpr, window.devicePixelRatio || 1));
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    composer?.setSize(w, h); bloomPass?.setSize(w, h);
  }
  const ro = new ResizeObserver(resize); ro.observe(container); resize();

  // apply shadow tier
  function applyTier(q) {
    const t = TIERS[q] || TIERS.high; quality = q;
    renderer.shadowMap.enabled = t.shadow > 0; sun.castShadow = t.shadow > 0;
    if (t.shadow > 0) sun.shadow.mapSize.set(t.shadow, t.shadow);
    postOn = t.bloom; if (postOn && !composer) buildComposer();
    renderer.setPixelRatio(Math.min(t.dpr, window.devicePixelRatio || 1)); resize();
  }

  // ---- Loop ----
  let raf = 0, last = performance.now(), running = false, tsun = 0;
  const clampArea = (p) => { p.x = Math.max(-20, Math.min(20, p.x)); p.z = Math.max(-3.0, Math.min(10, p.z)); };
  const resolveObstacles = (p) => { for (const o of obstacles) { const dx = p.x - o.x, dz = p.z - o.z; const d = Math.hypot(dx, dz); if (d < o.r && d > 0.0001) { p.x = o.x + (dx / d) * o.r; p.z = o.z + (dz / d) * o.r; } } };
  const forward = new THREE.Vector3(), right = new THREE.Vector3(), moveV = new THREE.Vector3();

  function frame(now) {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.05, (now - last) / 1000); last = now;
    // input axis
    let ax = 0, ay = 0;
    if (keys["w"] || keys["arrowup"]) ay += 1; if (keys["s"] || keys["arrowdown"]) ay -= 1;
    if (keys["d"] || keys["arrowright"]) ax += 1; if (keys["a"] || keys["arrowleft"]) ax -= 1;
    if (joyAxis.x || joyAxis.y) { ax = joyAxis.x; ay = joyAxis.y; }
    const mag = Math.hypot(ax, ay); const moving = mag > 0.08;
    forward.set(-Math.sin(camYaw), 0, -Math.cos(camYaw));
    right.set(Math.cos(camYaw), 0, -Math.sin(camYaw));
    moveV.set(0, 0, 0);
    if (moving) {
      moveV.addScaledVector(forward, ay).addScaledVector(right, ax);
      moveV.normalize(); const sp = 3.4 * Math.min(1, mag);
      player.group.position.addScaledVector(moveV, sp * dt);
      clampArea(player.group.position); resolveObstacles(player.group.position);
      playerYaw = Math.atan2(moveV.x, moveV.z);
    }
    // face
    let dy = playerYaw - player.group.rotation.y; while (dy > Math.PI) dy -= Math.PI * 2; while (dy < -Math.PI) dy += Math.PI * 2;
    player.group.rotation.y += dy * Math.min(1, dt * 12);
    player.update(dt, moving, Math.min(1, mag));

    // pet follows
    const pp = pet.group.position; const desired = new THREE.Vector3(player.group.position.x - Math.sin(playerYaw) * -1.1, 0, player.group.position.z - Math.cos(playerYaw) * -1.1);
    const pdx = desired.x - pp.x, pdz = desired.z - pp.z; const pd = Math.hypot(pdx, pdz); const petMoving = pd > 0.4;
    if (petMoving) { pp.x += (pdx / pd) * Math.min(pd, 3.6 * dt); pp.z += (pdz / pd) * Math.min(pd, 3.6 * dt); pet.group.rotation.y = Math.atan2(pdx, pdz); }
    pet.update(dt, petMoving);

    // pedestrians
    for (const p of peds) {
      const g = p.h.group; const tx = p.target.x - g.position.x, tz = p.target.z - g.position.z; const d = Math.hypot(tx, tz);
      if (d < 0.6) p.target = pickWalkTarget();
      else { g.position.x += (tx / d) * p.speed * dt; g.position.z += (tz / d) * p.speed * dt; const wy = Math.atan2(tx, tz); let ddy = wy - g.rotation.y; while (ddy > Math.PI) ddy -= Math.PI * 2; while (ddy < -Math.PI) ddy += Math.PI * 2; g.rotation.y += ddy * Math.min(1, dt * 8); }
      p.h.update(dt, d >= 0.6, p.speed / 1.5);
    }

    // proximity
    let best = null, bestD = 1e9;
    for (const it of interactables) { const d = Math.hypot(player.group.position.x - it.x, player.group.position.z - it.z); if (d < it.r && d < bestD) { best = it; bestD = d; } }
    if ((best?.id || null) !== (nearest?.id || null)) { nearest = best; onProximity?.(best || null); }

    // camera
    const off = new THREE.Vector3(Math.sin(camYaw) * camDist * Math.cos(camPitch), camDist * Math.sin(camPitch) + 1.0, Math.cos(camYaw) * camDist * Math.cos(camPitch));
    camTarget.copy(player.group.position).add(off);
    camera.position.lerp(camTarget, Math.min(1, dt * 6));
    camera.lookAt(player.group.position.x, player.group.position.y + 1.1, player.group.position.z);
    sky.position.copy(camera.position);

    // subtle lamp flicker at dusk-ish (static day, keep bulbs lit softly)
    tsun += dt;

    if (postOn && composer) composer.render(); else renderer.render(scene, camera);
  }

  return {
    start() { if (running) return; running = true; last = performance.now(); raf = requestAnimationFrame(frame); },
    stop() { running = false; cancelAnimationFrame(raf); },
    setMoveAxis(x, y) { joyAxis = { x, y }; },
    setQuality(q) { applyTier(q); },
    resize,
    tryInteract,
    dispose() {
      cancelAnimationFrame(raf); ro.disconnect();
      window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); window.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointerdown", onPointerDown); el.removeEventListener("pointermove", onPointerMove); el.removeEventListener("wheel", onWheel);
      renderer.dispose(); pmrem.dispose(); if (el.parentNode) el.parentNode.removeChild(el);
    },
  };
}
